import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import PairChart from '../PairChart'
import TokenLogo from '../TokenLogo'
import { formattedNum } from '../../utils'

const ChartSummaryContainer = styled.div`
  display: flex;
  gap: 20px;
  align-items: stretch;
  width: 100%;
  margin-bottom: 32px;
  @media screen and (max-width: 1024px) {
    flex-direction: column;
  }
`

const ChartCard = styled.div`
  background: #1b1c21;
  border-radius: 16px;
  flex: 1;
  position: relative;

  @media screen and (max-width: 1024px) {
    width: 100%;
  }
`

const StatsCard = styled.div`
  background: #1b1c21;
  border-radius: 16px;
  padding: 32px;
  width: 364px;
  height: 481px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-shrink: 0;
`

const TokenSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`

const SectionTitle = styled.div`
  font-family: 'Inter Display', sans-serif;
  font-weight: 500;
  font-size: 18px;
  color: white;
  line-height: 1.4;
`

const TokenList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const TokenRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`

const TokenInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const TokenName = styled.div`
  font-family: 'Inter Display', sans-serif;
  font-weight: 600;
  font-size: 18px;
  color: white;
  line-height: 1;
`

const TokenAmount = styled.div`
  font-family: 'Inter Display', sans-serif;
  font-weight: 600;
  font-size: 18px;
  color: white;
  line-height: 1;
  letter-spacing: -0.18px;
`

const Divider = styled.div`
  background: #222328;
  height: 1px;
  width: 100%;
`

const MetricsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const MetricRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`

const MetricInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 121px;
`

const MetricLabel = styled.div`
  font-family: 'Inter Display', sans-serif;
  font-weight: 500;
  font-size: 14px;
  color: white;
  opacity: 0.5;
  line-height: 1.4;
`

const MetricValue = styled.div`
  font-family: 'Inter Display', sans-serif;
  font-weight: 700;
  font-size: 24px;
  color: white;
  line-height: 1;
`

const ChangeIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const ChangeText = styled.div`
  font-family: 'Inter Display', sans-serif;
  font-weight: 600;
  font-size: 18px;
  line-height: 1;
  color: ${({ positive }) => (positive ? '#45e560' : '#ff8080')};
`

const ChangeArrow = styled.div`
  width: 5px;
  height: 3px;

  &::before {
    content: '';
    display: block;
    width: 0;
    height: 0;
    border-left: 2.5px solid transparent;
    border-right: 2.5px solid transparent;
    border-bottom: 3px solid ${({ positive }) => (positive ? '#45e560' : '#ff8080')};
    transform: ${({ positive }) => (positive ? 'rotate(0deg)' : 'rotate(180deg)')};
  }
`

function ChartSummary({ pairAddress, token0, token1, reserve0, reserve1, formattedLiquidity, volume, volumeChange, fees }) {
  // Parse percentage changes
  const parseChange = (changeStr) => {
    if (!changeStr && changeStr !== 0) return { value: '0.00%', isPositive: false }

    // Convert to string if it's a number
    const changeString = typeof changeStr === 'string' ? changeStr : String(changeStr)

    // Handle NaN values
    if (changeString === 'NaN' || changeString.includes('NaN')) {
      return { value: '0.00%', isPositive: true }
    }

    const cleanStr = changeString.replace(/[%+\-\s]/g, '')
    const numValue = parseFloat(cleanStr)

    // Check if the parsed number is NaN
    if (isNaN(numValue)) {
      return { value: '0.00%', isPositive: true }
    }

    const isPositive = !changeString.includes('-') && numValue >= 0
    return { value: `${isPositive ? '+' : ''}${numValue.toFixed(2)}%`, isPositive }
  }

  const volumeChangeData = parseChange(volumeChange)

  const totalValueLockedUSD = formattedLiquidity || '$0'

  // For now, use a placeholder for TVL change - this would come from actual data
  const tvlChangeData = parseChange('-0.26') // Placeholder value to match screenshot
  const feesChangeData = parseChange('0.26') // Different change for fees

  return (
    <ChartSummaryContainer>
      <ChartCard>
        <PairChart address={pairAddress} color={'#B3B3BE'} base0={reserve1 / reserve0} base1={reserve0 / reserve1} />
      </ChartCard>

      <StatsCard>
        <TokenSection>
          <SectionTitle>Total Tokens Locked</SectionTitle>
          <TokenList>
            <TokenRow>
              <TokenInfo>
                <TokenLogo address={token0?.id} size="24px" />
                <TokenName>{token0?.symbol || 'IXS'}</TokenName>
              </TokenInfo>
              <TokenAmount>{reserve0 ? formattedNum(reserve0, false) : '0'}</TokenAmount>
            </TokenRow>

            <Divider />

            <TokenRow>
              <TokenInfo>
                <TokenLogo address={token1?.id} size="24px" />
                <TokenName>{token1?.symbol || 'USDC'}</TokenName>
              </TokenInfo>
              <TokenAmount>{reserve1 ? formattedNum(reserve1, false) : '0'}</TokenAmount>
            </TokenRow>
          </TokenList>
        </TokenSection>

        <MetricsSection>
          <Divider />
          <MetricRow>
            <MetricInfo>
              <MetricLabel>TVL</MetricLabel>
              <MetricValue>{totalValueLockedUSD}</MetricValue>
            </MetricInfo>
            <ChangeIndicator>
              <ChangeText positive={tvlChangeData.isPositive}>{tvlChangeData.value}</ChangeText>
              <ChangeArrow positive={tvlChangeData.isPositive} />
            </ChangeIndicator>
          </MetricRow>

          <Divider />
          <MetricRow>
            <MetricInfo>
              <MetricLabel>24h Volume</MetricLabel>
              <MetricValue>{volume ? formattedNum(volume, true) : '$649.24'}</MetricValue>
            </MetricInfo>
            <ChangeIndicator>
              <ChangeText positive={volumeChangeData.isPositive}>{volumeChangeData.value}</ChangeText>
              <ChangeArrow positive={volumeChangeData.isPositive} />
            </ChangeIndicator>
          </MetricRow>
          <Divider />
          <MetricRow>
            <MetricInfo>
              <MetricLabel>24h Fees</MetricLabel>
              <MetricValue>{fees ? formattedNum(fees, true) : '$1.99'}</MetricValue>
            </MetricInfo>
            <ChangeIndicator>
              <ChangeText positive={feesChangeData.isPositive}>{feesChangeData.value}</ChangeText>
              <ChangeArrow positive={feesChangeData.isPositive} />
            </ChangeIndicator>
          </MetricRow>
        </MetricsSection>
      </StatsCard>
    </ChartSummaryContainer>
  )
}

ChartSummary.propTypes = {
  pairAddress: PropTypes.string,
  token0: PropTypes.shape({
    id: PropTypes.string,
    symbol: PropTypes.string,
  }),
  token1: PropTypes.shape({
    id: PropTypes.string,
    symbol: PropTypes.string,
  }),
  reserve0: PropTypes.string,
  reserve1: PropTypes.string,
  volume: PropTypes.string,
  volumeChange: PropTypes.string,
  fees: PropTypes.string,
  formattedLiquidity: PropTypes.string,
}

export default ChartSummary
